import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Globe, Route, Leaf, Wrench } from "lucide-react";

/** Saved workflow value. Older/unknown values may still exist on cameras, so this is a plain string. */
export type AiWorkflowType = string;

export const WORKFLOW_OPTIONS: { value: string; label: string; description: string; icon: React.ElementType }[] = [
  { value: 'universal-uk-v1', label: 'Universal', description: 'General-purpose image processing', icon: Globe },
  { value: 'highways-uk-v1', label: 'Highways', description: 'Roads, streetworks & traffic', icon: Route },
  { value: 'environmental-uk-v1', label: 'Environmental', description: 'Flood, water & environment', icon: Leaf },
  { value: 'custom_workflow', label: 'Custom', description: 'Organisation-specific workflow', icon: Wrench },
];

export const isKnownWorkflow = (v?: string | null) => !!v && WORKFLOW_OPTIONS.some(o => o.value === v);

interface WorkflowSelectorProps {
  value: AiWorkflowType;
  onChange: (value: AiWorkflowType) => void;
  disabled?: boolean;
  customWorkflowAvailable?: boolean;
}

export const WorkflowSelector = ({ value, onChange, disabled, customWorkflowAvailable = false }: WorkflowSelectorProps) => {
  const selected = isKnownWorkflow(value) ? value : "";

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">Workflow</Label>
      <Select value={selected} onValueChange={(v) => onChange(v)} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select workflow" />
        </SelectTrigger>
        <SelectContent>
          {WORKFLOW_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isDisabled = opt.value === 'custom_workflow' && !customWorkflowAvailable;
            return (
              <SelectItem key={opt.value} value={opt.value} disabled={isDisabled}>
                <div className={`flex items-center gap-2 ${isDisabled ? 'opacity-50' : ''}`}>
                  <Icon className="h-4 w-4 text-brand-500 shrink-0" />
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {opt.label}
                      {isDisabled ? ' (Not Configured)' : ''}
                    </span>
                    <span className="text-xs text-muted-foreground">{opt.description}</span>
                  </div>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};
